import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown, LogOut, Search, ShoppingCart, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import dqsLogo from "@/assets/dqs-logo.png";

const SearchDialog = lazy(() =>
  import("@/components/header/SearchDialog").then((m) => ({ default: m.SearchDialog })),
);
const CartDropdown = lazy(() =>
  import("@/components/header/CartDropdown").then((m) => ({ default: m.CartDropdown })),
);
const LanguageSelector = lazy(() =>
  import("@/components/header/LanguageSelector").then((m) => ({ default: m.LanguageSelector })),
);

const HeaderWidgetFallback = () => <div className="h-9 w-9" aria-hidden="true" />;

type CategoryKey = 'quality' | 'environment' | 'workSafety' | 'infoSecurity' | 'automotive';

const categoryItems: { nameKey: CategoryKey; hrefPt: string; hrefEn: string }[] = [
  { nameKey: 'quality', hrefPt: "/qualidade", hrefEn: "/quality" },
  { nameKey: 'environment', hrefPt: "/meio-ambiente", hrefEn: "/environment" },
  { nameKey: 'workSafety', hrefPt: "/seguranca-do-trabalho", hrefEn: "/occupational-health-safety" },
  { nameKey: 'infoSecurity', hrefPt: "/seguranca-da-informacao", hrefEn: "/information-security" },
  { nameKey: 'automotive', hrefPt: "/automotivo", hrefEn: "/automotive" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language, getLocalizedRoute } = useLanguage();
  const navigate = useNavigate();
  const [canSeeLeads, setCanSeeLeads] = useState(false);

  useEffect(() => {
    if (!user) { setCanSeeLeads(false); return; }
    Promise.all([
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "vendas" }),
    ]).then(([admin, vendas]) => {
      setCanSeeLeads(!!admin.data || !!vendas.data);
    });
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Get localized category href
  const getCategoryHref = (category: typeof categoryItems[number]) => {
    return language === 'en' ? category.hrefEn : category.hrefPt;
  };

  const navigation = [
    { name: t.home, href: "/" },
    { name: t.courses, href: getLocalizedRoute('catalog') },
  ];

  const navigationAfterCatalog = [
    { name: t.forCompanies, href: getLocalizedRoute('companies') },
    { name: t.about, href: getLocalizedRoute('about') },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container-dqs" aria-label="Global">
        <div className="flex h-16 items-center justify-between desktop:h-20">
          {/* Logo - Fixed size, left aligned */}
          <div className="flex shrink-0 items-center">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={dqsLogo} 
                alt="DQS" 
                className="shrink-0"
                style={{ 
                  height: '40px', 
                  width: 'auto',
                  minHeight: '40px', 
                  maxHeight: '40px',
                  objectFit: 'contain'
                }}
              />
              <div className="hidden sm:flex flex-col border-l border-border pl-3 shrink-0" style={{ minWidth: 'fit-content' }}>
                <span className="font-semibold text-primary text-base leading-tight whitespace-nowrap">Academy</span>
                <span className="text-xs text-muted-foreground leading-tight whitespace-nowrap">Brasil</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered, always visible on desktop (>=1200px) */}
          <div className="hidden desktop:flex items-center justify-center gap-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              >
                {item.name}
              </Link>
            ))}

            {/* Categorias Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                  {t.categories}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-popover">
                {categoryItems.map((category) => (
                  <DropdownMenuItem key={category.nameKey} asChild>
                    <Link to={getCategoryHref(category)} className="w-full cursor-pointer">
                      {t[category.nameKey]}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {navigationAfterCatalog.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex shrink-0 items-center ml-auto desktop:ml-0">
            {/* Desktop: Organized groups with separators */}
            <div className="hidden desktop:flex items-center">
              {/* Group A: Search, Cart & Language */}
              <div className="flex items-center gap-3">
                <Suspense fallback={<HeaderWidgetFallback />}>
                  <SearchDialog />
                </Suspense>
                <Suspense fallback={<HeaderWidgetFallback />}>
                  <CartDropdown />
                </Suspense>
                <Suspense fallback={<HeaderWidgetFallback />}>
                  <LanguageSelector />
                </Suspense>
              </div>
              
              {/* Separator before Auth */}
              <Separator orientation="vertical" className="mx-4 h-5 bg-border/50" />
              
              {/* Group B: Auth */}
              <div className="flex items-center gap-3">
                {isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                        <User className="h-5 w-5" />
                        <span>
                          {user?.name.split(" ")[0]}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-popover">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to={`${getLocalizedRoute('myCourses')}?tab=cursos`} className="w-full cursor-pointer">
                          {t.myCourses}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`${getLocalizedRoute('myCourses')}?tab=perfil`} className="w-full cursor-pointer">
                          {t.myProfile}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`${getLocalizedRoute('myCourses')}?tab=certificados`} className="w-full cursor-pointer">
                          {t.certificates}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/suporte" className="w-full cursor-pointer">
                          {t.helpAndSupport}
                        </Link>
                      </DropdownMenuItem>
                      {canSeeLeads && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin/leads" className="w-full cursor-pointer">
                            <Users className="h-4 w-4 mr-2" />
                            Leads B2B
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {/* Botão Sair com hover sutil — antes pintava um bloco
                          vermelho gigante por usar focus:bg-accent + text-destructive
                          combinados de forma estranha. Agora bg vermelho 10% no hover. */}
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t.logout}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <User className="h-5 w-5" />
                      {t.login}
                    </Link>
                    <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground px-4" asChild>
                      <Link to={getLocalizedRoute('register')}>
                        {t.register}
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Mobile menu button - only visible below desktop breakpoint */}
            <div className="flex desktop:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Abrir menu</span>
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu - only visible below 1200px */}
        <div
          className={cn(
            "desktop:hidden overflow-hidden transition-all duration-300 ease-in-out",
            mobileMenuOpen ? "max-h-[800px] pb-4" : "max-h-0"
          )}
        >
          <div className="space-y-1 pt-2">
            {/* Mobile Search */}
            <div className="px-2 pb-2">
              <Suspense fallback={<HeaderWidgetFallback />}>
                <SearchDialog />
              </Suspense>
            </div>

            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}

            {/* Mobile Categorias Collapsible */}
            <Collapsible open={catalogOpen} onOpenChange={setCatalogOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md">
                <span>{t.categories}</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", catalogOpen && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4">
                {categoryItems.map((category) => (
                  <Link
                    key={category.nameKey}
                    to={getCategoryHref(category)}
                    className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setCatalogOpen(false);
                    }}
                  >
                    {t[category.nameKey]}
                  </Link>
                ))}
              </CollapsibleContent>
            </Collapsible>

            {navigationAfterCatalog.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Auth buttons */}
            <div className="flex flex-col gap-2 pt-4 px-2 sm:hidden">
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 border-t border-border">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <Link
                    to={`${getLocalizedRoute('myCourses')}?tab=cursos`}
                    className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.myCourses}
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t.logout}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <User className="h-4 w-4 mr-2" />
                      {t.login}
                    </Link>
                  </Button>
                  <Button className="w-full" asChild>
                    <Link to={getLocalizedRoute('register')} onClick={() => setMobileMenuOpen(false)}>
                      {t.register}
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
